import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';
import {useConfigurationStore} from '../../store/pinia/configuration';
import {required} from '@vuelidate/validators';
import {DataService} from '../../api';
import {cloneDeep} from 'lodash';
import {StringAttribute} from '../../components/container/attributes/StringAttribute.ts';
import {BooleanAttribute} from '../../components/container/attributes/BooleanAttribute.ts';
import {RichTextAttribute} from '../../components/container/attributes/RichTextAttribute.ts';
import {TagAttribute} from '../../components/container/attributes/TagAttribute.ts';
import {NotFetchedAttribute} from '../../components/container/attributes/NotFetchedAttribute.ts';
import {FkBaseAttribute} from '../../components/container/attributes/join/FkBaseAttribute';
import {FkArrayBaseAttribute} from '../../components/container/attributes/join/FkArrayBaseAttribute';

export default class BaseModel {
  constructor(id, number, designation) {
    this.id = id;
    this.number = number;
    this.designation = designation;
  }

  static get baseModuleForRedirection() {
    return 'set-the-module-name-in-child-class';
  }
  get baseModuleForRedirection() {
    return BaseModel.baseModuleForRedirection;
  }

  static get attrsToStoreInForm() {
    return ['number', 'designation'];
  }
  get attrsToStoreInForm() {
    return BaseModel.attrsToStoreInForm;
  }

  static get joinFilterColumns() {
    return ['no', 'designation'];
  }
  get joinFilterColumns() {
    return BaseModel.joinFilterColumns;
  }

  static get config() {
    return {
      addable: true,
      duplicatable: true,
      importable: true,
      browsable: true,
      deletable: true,
    };
  }

  get config() {
    return BaseModel.config;
  }

  /**
   * Define print template prefix in child class if entity is printable
   */
  static get printTemplateType() {
    return undefined;
  }
  get printTemplateType() {
    return BaseModel.printTemplateType;
  }

  /** Override in the child class */
  static getPrefixIcon(_entity) {
    return null;
  }

  get prefixIcon() {
    return BaseModel.getPrefixIcon(this);
  }

  /** The short designation is the minimum to display to recognize the entity. If a number is defined, it is used instead of the designation */
  get shortDesignation() {
    return this.number || this.designation;
  }

  /**
   * The full designation is per default <no - designation> or <designation>
   * depending on the value of no. If entity has other convention -> override
   */
  get fullDesignation() {
    return HitUtils.createStandardDesignation(this);
  }

  /**
   * The entity designation is only the designation part of the entity and not
   * with the linked parts from an eventual parent entity (as for project parts)
   */
  get entityDesignation() {
    return this.fullDesignation;
  }

  /**
   * Designation displayed in the dropdown part of the fast mode browser
   * By default, it is the same as the full designation
   */
  get fastModeBrowserDesignation() {
    return this.fullDesignation;
  }

  /**
   * Name of the table the entity belongs to
   */
  static get apiRoute() {
    throw Error('API route needs to be defined in the child class');
  }
  get apiRoute() {
    throw Error('API route needs to be defined in the child class');
  }

  /**
   * When an entity is used in another table, this is the default name of the FK
   * If another name is used, set the fkToUpdate prop in the attribute
   */
  static get fkName() {
    throw Error('Foreign key name needs to be defined in the child class');
  }
  get fkName() {
    throw Error('Foreign key name needs to be defined in the child class');
  }

  static get listPageRoute() {
    throw Error('List page route must be defined in the child class');
  }
  get listPageRoute() {
    throw Error('List page route must be defined in the child class');
  }

  /**
   * Name of the view the user is redirected to if he requests details about the entity
   */
  static get detailPageRoute() {
    throw Error('Detail page must be defined in the child class');
  }
  get detailPageRoute() {
    throw Error('Detail page must be defined in the child class');
  }

  /**
   * Initial values for the columns if the entity is created via the creation
   * button in the floating panel
   */
  static get createInitColumns() {
    return {
      designation: '',
    };
  }

  /**
   * Initial values for the columns if the entity is created via the creation
   * button in the floating panel
   * We also need to define a non-static getter -> ex.: projectPart needs to set
   * the project_id during creation -> needs to access instance of the class
   */
  get createInitColumns() {
    return BaseModel.createInitColumns;
  }

  /**
   * When we join an entity during fetch process, we do not always want to get all
   * the values from the entity. Per default = all.
   * TO IMPROVE PERFORMANCE -> Return only a list of attributes to limit join
   */
  static get joinAttrsKey() {
    throw Error(
      'Missing ovveride of function joinAttrsKey in child class that extends the BaseModel!',
    );
  }

  /**
   * Only join the necessary attributes for the designation
   */
  static get joinAttrsForDesignation() {
    const designationKeys = ['number', 'designation'].map((key) =>
      this.allAttributes.find((attr) => attr.name === key),
    );
    return designationKeys.filter((attr) => attr !== undefined);
  }

  static get orderKey() {
    return 'no';
  }
  static get orderOrder() {
    return 'asc';
  }

  // BaseModel default generic dataListConfig (can be overwritten in child class, see example in Staff.js)
  static get dataListConfig() {
    return {
      zone2CustomFunction: (data) => {
        const tempInstance = new this(data);
        return tempInstance.fullDesignation;
      },
    };
  }

  static get orderKeyEmbedded() {
    return 'number';
  }

  static get listDisplayAttrs() {
    throw Error(
      'Define the default list display attributes in the child class',
    );
  }

  static get entityListDisplayAttrs() {
    throw Error(
      'Define the default entity list display attributes in the child class',
    );
  }

  static get detailAttrs() {
    throw Error('Define the detail attributes in the child class');
  }

  static get browserDisplayAttrs() {
    throw Error(
      'Define the attributes that need to be loaded in the entity browser in the child class',
    );
  }

  static get confidentialBrowserDisplayAttrs() {
    return this.browserDisplayAttrs;
  }

  static get bulkUpdateAttrs() {
    return undefined;
  }

  /**
   * Entities that link staff members to qualifications via a {entity}_staff table
   * (e.g. project_staff) can expose role bulk-update in the bulk edit modal.
   */
  static get hasEntityStaffRolesBulkUpdate() {
    return false;
  }

  static get entityStaffRolesBulkUpdateAttrs() {
    if (!this.hasEntityStaffRolesBulkUpdate) {
      return {};
    }
    return {
      'hit-base.common.role': [
        {
          name: 'roles',
          tableProperties: {
            header: 'hit-base.common.role',
          },
        },
      ],
    };
  }

  /** Returns the requested attributes:
   * - displayAttributes; with tableProperties to be displayed in a table
   * - fetchAttributes: tableProperties are deleted to only fetch data without displaying it
   */
  static getAttributes(displayAttributes = [], fetchAttributes = []) {
    const attributes = [];
    displayAttributes.forEach((aKey) => {
      attributes.push(this.allAttributes.find((attr) => attr.name === aKey));
    });
    fetchAttributes.forEach((aKey) => {
      const att = cloneDeep(
        this.allAttributes.find((attr) => attr.name === aKey),
      );
      delete att.tableProperties;
      attributes.push(att);
    });
    return attributes;
  }

  /** The list of all the possible attributes are defined in the child class for each model */
  static get allAttributes() {
    return [];
  }

  /**
   * Generates a list with all the columns that are also fetched with the modal browser
   * Like this, even if the data is not displayed, it is returned when an entity has been selected
   * Important for the form functionality
   */
  static entityColumns(attributes = this.browserDisplayAttrs) {
    let attributeString = '';
    let fetchedIdColumn = false;
    attributes.forEach((att) => {
      if (att instanceof TagAttribute || att instanceof NotFetchedAttribute)
        return;

      if (att instanceof FkBaseAttribute) {
        let fkColumns = '';
        att.joinPath.forEach((join) => {
          fkColumns += join + '(';
        });
        fkColumns += att.dataObjectClass.entityColumns(
          att.dataObjectClass.getAttributes(att.dataObjectClass.joinAttrsKey),
        );
        fkColumns = fkColumns + ')'.repeat(att.joinPath.length);
        attributeString += `${att.name}:${fkColumns},`;
      } else if (att instanceof FkArrayBaseAttribute) {
        let fkColumns = '';
        att.joinPath.forEach((join) => {
          fkColumns += join + '(';
        });
        fkColumns += att.dataObjectClass.entityColumns(
          att.dataObjectClass.getAttributes(att.dataObjectClass.joinAttrsKey),
        );
        fkColumns = fkColumns + ')'.repeat(att.joinPath.length);
        attributeString += `${att.name}:${fkColumns},`;
      } else {
        const column = att.column ? att.column : att.name;
        if (column === 'id') fetchedIdColumn = true;
        attributeString += `${att.name}:${column},`;
      }
    });

    if (!fetchedIdColumn) attributeString += 'id,';
    return attributeString.slice(0, -1);
  }

  /**
   * Generates the given attribute in all the selected languages of the company
   */
  static generateMultiLanguageField({
    key,
    header,
    attributeClass,
    column = null,
  }) {
    const attributes = [];
    const languages = useConfigurationStore().companyLanguagesObject;
    languages.forEach((lan) => {
      let headerValue = header;
      if (languages.length > 1) {
        headerValue = {
          message: `${header}-localised`,
          payload: {
            locale: lan.shortDesignation,
          },
        };
      }
      attributes.push(
        new attributeClass({
          name: `${key}${lan.valueSuffix}`,
          column: `${column || key}_${lan.hitI18nCode}`,
          validations: {required},
          formItemName: key,
          tableProperties: {
            header: headerValue,
          },
        }),
      );
    });
    return attributes;
  }

  /**
   * Generates the attribute name for the user language
   */
  static attributeUserLocale(attributePrefix, snakeCase = false) {
    let userLanguage;
    if (snakeCase) {
      userLanguage = '_' + useConfigurationStore().userLanguageSnakeCase;
    } else {
      userLanguage = useConfigurationStore().userLanguagePascalCase;
    }
    return `${attributePrefix}${userLanguage}`;
  }

  /**
   * Generates the attribute name for the company language
   */
  static attributeCompanyLocale(attributePrefix, snakeCase = false) {
    let companyLanguage;
    if (snakeCase) {
      companyLanguage = '_' + useConfigurationStore().mainLanguageSnakeCase;
    } else {
      companyLanguage = useConfigurationStore().mainLanguagePascalCase;
    }
    return `${attributePrefix}${companyLanguage}`;
  }

  static attributesAllCompanyLanguages(attributePrefix, snakeCase = false) {
    const attributes = [];
    useConfigurationStore().companyLanguagesObject.forEach((lan) => {
      attributes.push(
        snakeCase
          ? `${attributePrefix}_${lan.hitI18nCode}`
          : `${attributePrefix}${lan.valueSuffix}`,
      );
    });
    return attributes;
  }

  static factory() {
    throw Error(
      `Factory method should be implemented in each subclass of BaseHitModel: ${this.name}`,
    );
  }
  static fromJson(json) {
    let result = this.factory();
    json = this.$transformJson(json);
    result = Object.assign(result, json);
    return result;
  }

  static $transformJson(json) {
    Object.keys(json).forEach((property) => {
      // Change dates to Date type instead of String
      if (property.toUpperCase().endsWith('DATE')) {
        json[property] = new Date(json[property]);
      }
    });

    return json;
  }

  /**
   * Keep them here -> form store still uses them
   */
  static getMTMRelations() {
    return {};
  }

  static getMTORelations() {
    return {};
  }

  static getIdColumn() {
    return 'id';
  }

  /**
   * Needs to be done in separate function to be able to overwrite function in
   * case of project part -> list view is the detail view of the project
   */
  redirectToList(router) {
    router.push({
      name: this.listPageRoute,
    });
  }

  redirectToDetailPage(router) {
    if (router && this.detailPageRoute && this.id) {
      router.push({
        name: this.detailPageRoute,
        params: {
          id: this.id,
        },
      });
    }
  }

  async delete(router = undefined) {
    await DataService.delete(this.apiRoute, {
      id: `eq.${this.id}`,
    });
    if (router) {
      this.redirectToList(router);
    }
    return true;
  }

  static async loadFormDataModel(id) {
    const route = this.apiRoute;
    const res = await DataService.read(route, {
      attributes: this.entityColumns(),
      filters: {id: `eq.${id}`},
    });
    if (res?.data?.length > 0) {
      const dataModel = new this(res.data[0]);
      return this.transformToDbColumns(dataModel, this);
    }
    return {};
  }

  static async loadEntityFromId(id) {
    const res = await DataService.read(this.apiRoute, {
      attributes: this.entityColumns(),
      filters: {id: `eq.${id}`},
    });
    if (res?.data?.length > 0) {
      return new this(res.data[0]);
    }
    return null;
  }

  /**
   * Loads one or multiple entities from the entity type
   * If limit is set to null or undefined, all entries are loaded from the database
   */
  static async loadCustomEntity(limit = 1, customFilters = {}) {
    const params = {
      attributes: this.entityColumns(),
      filters: customFilters,
    };
    if (limit) {
      params['limit'] = limit;
    }
    const res = await DataService.read(this.apiRoute, params);
    if (res?.data?.length > 0) {
      if (limit === 1) {
        return new this(res.data[0]);
      }
      return res.data.map((item) => new this(item));
    }
    return [];
  }

  // TODO: duplicated code from HitContainerFastModeBrowser
  static transformToDbColumns(data, dataClass) {
    if (!data) return {};
    const dataToStore = {};
    if ('id' in data) {
      dataToStore['id'] = data['id'];
    }
    dataClass.attrsToStoreInForm.forEach((attrKey) => {
      if (attrKey.includes('designation')) {
        dataToStore['designation'] = data?.designation;
      } else if (attrKey.includes('description')) {
        dataToStore['description'] = data?.description;
      } else {
        const a = dataClass.allAttributes.find((a) => a.name === attrKey);
        const storeKey = a?.formItemName || a?.column || attrKey;
        if (a instanceof FkBaseAttribute) {
          dataToStore[storeKey] = this.transformToDbColumns(
            data[attrKey],
            a.dataObjectClass,
          );
        } else {
          dataToStore[storeKey] = data?.[attrKey];
        }
      }
    });
    return dataToStore;
  }

  // % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % %
  // ATTRIBUTES THAT ARE REUSED ACCROSS DIFFERENT MODELS CAN BE DEFINED HERE TO AVOID MULTIPLE DEFINITIONS
  // If no options are passed, the options from this default definition are used
  // % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % % %
  static NUMBER_ATTRIBUTE(options = {}, tableProperties = {}) {
    return new StringAttribute({
      name: 'number',
      column: 'no',
      tableProperties: {
        header: 'hit-app.common.number',
        maxWidth: '0.5fr',
        ...tableProperties,
      },
      ...options,
    });
  }

  static DESIGNATION_ATTRIBUTE(options = {}, tableProperties = {}) {
    return new StringAttribute({
      name: 'designation',
      validations: {required},
      tableProperties: {
        header: 'hit-base.common.designation',
        ...tableProperties,
      },
      ...options,
    });
  }

  static ACTIVE_ATTRIBUTE(options = {}, tableProperties = {}) {
    return new BooleanAttribute({
      name: 'active',
      creationValue: true,
      tableProperties: {
        header: 'hit-app.common.active',
        maxWidth: '2.5rem',
        ...tableProperties,
      },
      ...options,
    });
  }

  static MEMO_RICH_TEXT_ATTRIBUTE(options = {}, tableProperties = {}) {
    return new RichTextAttribute({
      name: 'memo',
      tableProperties: {
        header: 'hit-base.common.memo',
        ...tableProperties,
      },
      ...options,
    });
  }

  static HEADER_RICH_TEXT_ATTRIBUTE(options = {}, tableProperties = {}) {
    return new RichTextAttribute({
      name: 'header',
      tableProperties: {
        header: 'hit-app.common.header',
        ...tableProperties,
      },
      ...options,
    });
  }

  static DESCRIPTION_RICH_TEXT_ATTRIBUTE(options = {}, tableProperties = {}) {
    return new RichTextAttribute({
      name: 'description',
      tableProperties: {
        header: 'hit-base.common.description',
        ...tableProperties,
      },
      ...options,
    });
  }

  static FOOTER_RICH_TEXT_ATTRIBUTE(options = {}, tableProperties = {}) {
    return new RichTextAttribute({
      name: 'footer',
      tableProperties: {
        header: 'hit-app.common.footer',
        ...tableProperties,
      },
      ...options,
    });
  }

  static TAGS_ATTRIBUTE(options = {}, tableProperties = {}) {
    return new TagAttribute({
      name: 'tags',
      tableProperties: {
        header: 'hit-base.common.tags',
        ...tableProperties,
      },
      ...options,
    });
  }
}
